/**
 * Self-service removal from the waitlist (Phase 118-06, compliance gap G15).
 *
 * Calls the public `waitlist-erase` edge function with the token from the
 * emailed link. The caller is a lead who may have no account, so nothing here
 * needs a session; `functions.invoke` still sends the anon key.
 *
 * The server answers an unknown, expired, reused or malformed link IDENTICALLY
 * (200 `{ removed: false }`), so this module can only ever tell the person
 * whether THEIR link worked — never whether an address is on the list.
 */

import { isBackendEnabled, isMockMode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { supabase } from '@/lib/supabase/client';

export type WaitlistRemovalOutcome =
  /** The lead and its details are gone. */
  | 'removed'
  /** The link was expired, already used, replaced by a newer email, or never valid. */
  | 'link_invalid'
  /** Too many attempts from this network — try again in an hour. */
  | 'rate_limited';

/** A failure the person can retry: the service was unreachable or errored. */
export class WaitlistRemovalError extends Error {
  constructor(message = 'We could not process this right now. Please try again shortly.') {
    super(message);
    this.name = 'WaitlistRemovalError';
  }
}

/** Read `?token=` robustly: expo-router hands a string, a string[] or nothing. */
export function readRemovalToken(param: string | string[] | undefined): string | null {
  const raw = Array.isArray(param) ? param[0] : param;
  const token = raw?.trim();
  return token ? token : null;
}

export async function removeFromWaitlist(token: string): Promise<WaitlistRemovalOutcome> {
  // No backend in mock mode: never pretend an erasure happened.
  if (!isBackendEnabled() || isMockMode()) {
    throw new WaitlistRemovalError('Waitlist removal is not available in this preview build.');
  }

  const { data, error } = await supabase.functions.invoke('waitlist-erase', { body: { token } });

  if (!error) {
    const removed = (data as { removed?: unknown } | null)?.removed;
    if (removed === true) return 'removed';
    if (removed === false) return 'link_invalid';
    reportError(new Error('waitlist-erase: unexpected response shape'), {
      context: 'removeFromWaitlist.response',
      level: 'warning',
    });
    throw new WaitlistRemovalError();
  }

  // Non-2xx: functions.invoke puts the Response on error.context.
  const status = (error as { context?: { status?: unknown } }).context?.status;
  if (status === 429) return 'rate_limited';

  reportError(error, { context: 'removeFromWaitlist.invoke', level: 'warning' });
  throw new WaitlistRemovalError();
}
